import React, { useEffect, useState } from "react";

import {
  UserOrderResponse,
} from "@/api";

import OrderDetailsView from "./OrderDetailsView";
import OrderDetailsEdit from "./OrderDetailsEdit";

import {
  AdminOrderUpdateService,
} from "../../service/adminOrderUpdateServices";

interface OrderDetailsSectionProps {
  orderId: number;

  value: UserOrderResponse;

  onChange: (
    value: UserOrderResponse
  ) => void;
}

export default function OrderDetailsSection({
  orderId,
  value,
  onChange,
}: OrderDetailsSectionProps) {
  const [editing, setEditing] =
    useState(false);

  const [draft, setDraft] =
    useState<UserOrderResponse>(value);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  /*
   * --------------------------------------------------
   * EDIT
   * --------------------------------------------------
   */

  const handleEdit = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  /*
   * --------------------------------------------------
   * SAVE
   * --------------------------------------------------
   */

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const updatedOrder =
        await AdminOrderUpdateService.update_order(
          orderId,
          {
            requestor_name:
              draft.requestor_name,

            requestor_email:
              draft.requestor_email,

            requestor_phone:
              draft.requestor_phone,
          }
        );

      /*
       * Backend response is authoritative.
       */
      onChange(updatedOrder);

      setEditing(false);

    } catch (err) {
      console.error(
        "Failed to update order",
        err,
      );

      setError(
        err instanceof Error
          ? err.message
          : "Failed to update order."
      );

    } finally {
      setSaving(false);
    }
  };

  /*
   * --------------------------------------------------
   * CANCEL
   * --------------------------------------------------
   */

  const handleCancel = () => {
    setDraft(value);
    setError(null);
    setEditing(false);
  };

  /*
   * --------------------------------------------------
   * SYNC WITH PARENT
   * --------------------------------------------------
   *
   * If another section causes the order DTO to
   * refresh, don't overwrite an active edit.
   */

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  return (
    <section className="rounded-xl border bg-white p-6">

      {editing ? (
        <>
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                Order Details
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Edit the requestor details.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="rounded-md border px-3 py-2 text-sm disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="rounded-md bg-black px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          <OrderDetailsEdit
            value={draft}
            onChange={setDraft}
          />

          {error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                Order Details
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Review the requestor details.
              </p>
            </div>

            <button
              type="button"
              onClick={handleEdit}
              className="rounded-md border px-3 py-2 text-sm font-medium"
            >
              Edit
            </button>
          </div>

          <OrderDetailsView
            value={value}
          />
        </>
      )}
    </section>
  );
}